import {
  CUSTOMIZE_AGGREGATE_CLICKED,
  MODAL_HIDDEN,
  MODAL_INTERACTION,
} from '@/actions/constants';
import { CUSTOMIZE_AGGREGATE, WHERE } from '@/util/enums/EnumLayers';
import AggregateFunction from '@/util/AggregateFunction';
import NewWorldColumn from '@/util/NewWorldColumn';
import InlineAggregateLayer from '@/components/layers/InlineAggregateLayer';

import LayerWhere from '@/components/layers/LayerWhere';
import {
  INITIAL_GLOBAL_STATE,
  updateClauses,
  stateForExistingLayer,
} from '@/reducers/modal_where';
import { buildLayer } from '@/reducers/layers';

export type ModalCustomizeAggregateState = {
  aggregateFunction: AggregateFunction;
  columns: NewWorldColumn[];
  getTableOrInsightName: F2<string, boolean, string>;
  currentLayer: Nullable<InlineAggregateLayer>;
  readyForSave: boolean;
  isColumnNameError: boolean;
  columnName?: string;
  clauses: LayerWhere[];
  selectedMetric?: number;
};

// @ts-expect-error: types mismatch. Infer a proper type.
const INITIAL_STATE: ModalCustomizeAggregateState = {
  columns: [],
  getTableOrInsightName: (): string => '',
  currentLayer: null,
  columnName: '',
  ...INITIAL_GLOBAL_STATE,
};

const modalCustomizeAggregateReducer = (state = INITIAL_STATE, action: any) => {
  switch (action.type) {
    case MODAL_HIDDEN:
      return INITIAL_STATE; // reset modal
    case CUSTOMIZE_AGGREGATE_CLICKED:
      return getInitialState(state, action);
    case MODAL_INTERACTION:
      return getUpdatedState(state, action);
    default:
      return state;
  }
};

const getInitialState = (state: ModalCustomizeAggregateState, action: any) => {
  const aggregateFunction = AggregateFunction.build(
    action.payload.aggregateFunction,
  );
  return {
    ...state,
    ...(action.payload.clauses &&
      stateForExistingLayer(action.payload.clauses)),
    aggregateFunction,
    columns: action.payload.columns,
    getTableOrInsightName: action.payload.getTableOrInsightName,
    currentLayer: action.payload.currentLayer,
    selectedMetric: action.payload.selectedMetric,
    columnName: aggregateFunction.metricColumnName,
    readyForSave: true,
  };
};

export const getUpdatedLayer = ({
  state,
}: {
  state: ModalCustomizeAggregateState;
}) => {
  const { currentLayer, aggregateFunction, selectedMetric, columnName } = state;
  const clauses = buildClauses(state);
  const updatedLayer = currentLayer?.cloneAndUpdateId(currentLayer.id());
  aggregateFunction.clauses = clauses;
  aggregateFunction.metricColumnName = columnName;
  const functions = [...updatedLayer._functions];
  if (typeof selectedMetric === 'number') {
    // selectedMetric could be 0
    functions[selectedMetric] = aggregateFunction;
  } else {
    functions.push(aggregateFunction);
  }
  updatedLayer._functions = functions;
  return updatedLayer;
};

const getUpdatedState = (state: ModalCustomizeAggregateState, action: any) => {
  if (action.payload.modalType !== CUSTOMIZE_AGGREGATE) {
    return state;
  }
  if (action.payload.props.metricRemoved) {
    return {
      ...state,
      aggregateFunction: undefined,
      readyForSave: false,
    };
  }

  if (action.payload.props.aggregateFunction) {
    const newState = {
      ...state,
      aggregateFunction: action.payload.props.aggregateFunction,
    };
    return {
      ...newState,
      readyForSave: isReadyForSave(newState),
    };
  }
  if (action.payload.props.columnNameChanged) {
    return updateColumnName(state, action);
  }

  const newState = updateClauses(state, action) as ModalCustomizeAggregateState;
  return {
    ...newState,
    readyForSave: isReadyForSave(newState),
  };
};

const updateColumnName = (state: ModalCustomizeAggregateState, action: any) => {
  const columnName: string = action.payload.props.columnName.replace(
    /[^A-Za-z0-9_]+/gi,
    '_',
  );
  const columnExists = !!state.columns.find(
    (c) => c.name().name().toLowerCase() === columnName.toLowerCase(),
  );

  const newState = {
    ...state,
    isColumnNameError: columnExists,
    columnName,
  };
  return {
    ...newState,
    readyForSave: isReadyForSave(newState),
  };
};

const buildClauses = (state: ModalCustomizeAggregateState) => {
  return state.clauses.every((c: any) => c.readyForSave)
    ? buildLayer(WHERE, state)
    : null;
};

const isReadyForSave = (state: ModalCustomizeAggregateState) =>
  !!state.aggregateFunction &&
  !state.isColumnNameError &&
  ((state.clauses.length === 1 &&
    state.clauses.every((c) => !c.lhs && !c.operator && !c.rhsValue)) ||
    state.clauses.every((c: any) => c.readyForSave));

export default modalCustomizeAggregateReducer;
